/**
 * src/three/stoneTexture.js
 * ผิวก้อนสี่เหลี่ยม — ลายหินหนาแน่นจัด รอยร้าว + รูพรุนเยอะมาก
 */
import * as THREE from 'three';

function hash(x, y, seed = 0) {
  let n = (x * 374761393 + y * 668265263 + seed * 982451653) | 0;
  n = (n ^ (n >>> 13)) * 1274126177;
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
}

/** ความใกล้รอยร้าว (0 = ตรงกลางรอย, 1 = ไม่ใกล้) */
function crackProximity(x, y, size, seed, crack) {
  const wobble = Math.sin(x * 0.065 + seed) * 8
    + Math.sin(y * 0.045 + seed * 0.7) * 6
    + Math.sin((x + y) * 0.028) * 4
    + Math.sin(x * 0.12 - y * 0.08) * 2.5;

  const lines = [
    [Math.abs(x - y * 0.92 + wobble), crack ? 4.8 : 3.8],
    [Math.abs(x * 0.7 + y - size * 0.48 + wobble * 0.6), crack ? 4.4 : 3.5],
    [Math.abs(y - size * 0.18 + Math.sin(x * 0.048) * 24), crack ? 4.0 : 3.2],
    [Math.abs(x - size * 0.5 + Math.sin(y * 0.052) * 22), crack ? 4.0 : 3.2],
    [Math.abs(y - size * 0.52 + Math.cos(x * 0.038) * 20), 3.0],
    [Math.abs(x + y * 0.45 - size * 0.92 + wobble * 0.4), 3.2],
    [Math.abs(x * 0.35 - y + size * 0.15 + Math.sin(x * 0.032) * 14), 2.9],
    [Math.abs(y - size * 0.75 + Math.sin(x * 0.075) * 12), 2.8],
    [Math.abs(x - size * 0.28 + Math.cos(y * 0.065) * 16), 2.9],
    [Math.abs(x * 0.5 + y * 0.8 - size * 0.62 + wobble * 0.25), 3.0],
    [Math.abs(y - x * 0.4 - size * 0.1 + Math.sin(x * 0.055) * 11), 2.7],
    [Math.abs(x - size * 0.78 + Math.sin(y * 0.085) * 13), 2.8],
    [Math.abs(y - size * 0.38 + Math.cos(x * 0.09) * 15), 2.6],
    [Math.abs(x - size * 0.15 + Math.sin(y * 0.04) * 18), 2.7],
    [Math.abs(x * 0.9 - y * 0.55 - size * 0.05 + wobble * 0.2), 2.8],
    [Math.abs(y + x * 0.25 - size * 0.88 + Math.sin(x * 0.06) * 9), 2.5],
  ];

  // กิ่งรอยร้าวสั้นๆ หนาแน่น
  const cell = 18;
  const cx = Math.floor(x / cell);
  const cy = Math.floor(y / cell);
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const gx = cx + ox;
      const gy = cy + oy;
      if (hash(gx, gy, seed + 200) < 0.62) {
        const px = (gx + 0.5) * cell + (hash(gx, gy, seed + 201) - 0.5) * cell * 0.7;
        const py = (gy + 0.5) * cell + (hash(gx, gy, seed + 202) - 0.5) * cell * 0.7;
        const ang = hash(gx, gy, seed + 203) * Math.PI * 2;
        const len = 14 + hash(gx, gy, seed + 204) * 32;
        const dx = x - px;
        const dy = y - py;
        const along = dx * Math.cos(ang) + dy * Math.sin(ang);
        const across = Math.abs(-dx * Math.sin(ang) + dy * Math.cos(ang));
        if (along > 0 && along < len) {
          lines.push([across, 1.8 + hash(gx, gy, seed + 205) * 1.8]);
        }
        // กิ่งแยกสองทาง
        if (hash(gx, gy, seed + 206) > 0.45) {
          const ang2 = ang + (hash(gx, gy, seed + 207) - 0.5) * 1.4;
          const along2 = dx * Math.cos(ang2) + dy * Math.sin(ang2);
          const across2 = Math.abs(-dx * Math.sin(ang2) + dy * Math.cos(ang2));
          if (along2 > 0 && along2 < len * 0.7) {
            lines.push([across2, 1.5 + hash(gx, gy, seed + 208) * 1.2]);
          }
        }
      }
    }
  }

  let best = 1;
  for (const [dist, thick] of lines) {
    if (dist < thick) best = Math.min(best, dist / thick);
  }
  return best;
}

/** ความลึกของรูพรุน (0 = ไม่มี, 1 = หลุมลึก) */
function pitDepth(x, y, seed) {
  let depth = 0;

  // หลุมใหญ่หนาแน่น
  const cell = 10;
  const cx = Math.floor(x / cell);
  const cy = Math.floor(y / cell);
  for (let oy = -2; oy <= 2; oy++) {
    for (let ox = -2; ox <= 2; ox++) {
      const gx = cx + ox;
      const gy = cy + oy;
      if (hash(gx, gy, seed + 300) > 0.32) continue; // ~68% มีหลุม
      const px = (gx + 0.3 + hash(gx, gy, seed + 301) * 0.4) * cell;
      const py = (gy + 0.3 + hash(gx, gy, seed + 302) * 0.4) * cell;
      const rad = 1.8 + hash(gx, gy, seed + 303) * 6.5;
      const d = Math.hypot(x - px, y - py);
      if (d < rad) {
        const t = 1 - d / rad;
        depth = Math.max(depth, t * t * (0.5 + hash(gx, gy, seed + 304) * 0.5));
      }
    }
  }

  // รูเล็กกระจายแน่นมาก
  const p1 = hash(x, y, seed + 77);
  const p2 = hash(x + 3, y - 2, seed + 88);
  const p3 = hash(x - 4, y + 5, seed + 99);
  const p4 = hash(x + 7, y + 1, seed + 110);
  const p5 = hash(x - 6, y - 5, seed + 115);
  const p6 = hash(x + 2, y + 8, seed + 118);
  if (p1 > 0.48) depth = Math.max(depth, (p1 - 0.48) / 0.52 * 0.9);
  if (p2 > 0.55) depth = Math.max(depth, (p2 - 0.55) / 0.45 * 0.8);
  if (p3 > 0.6) depth = Math.max(depth, (p3 - 0.6) / 0.4 * 0.75);
  if (p4 > 0.65) depth = Math.max(depth, (p4 - 0.65) / 0.35 * 0.7);
  if (p5 > 0.7) depth = Math.max(depth, (p5 - 0.7) / 0.3 * 0.65);
  if (p6 > 0.72) depth = Math.max(depth, (p6 - 0.72) / 0.28 * 0.6);

  // กระจุกรูจิ๋ว
  const micro = hash(x >> 1, y >> 1, seed + 120);
  if (micro > 0.4 && hash(x, y, seed + 121) > 0.35) {
    depth = Math.max(depth, (micro - 0.4) * 1.4);
  }
  const micro2 = hash(x >> 1, y >> 1, seed + 130);
  if (micro2 > 0.5 && hash(x + 1, y, seed + 131) > 0.4) {
    depth = Math.max(depth, (micro2 - 0.5) * 1.2);
  }

  return Math.min(1, depth);
}

function createHeightCanvas(size, seed, crack) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(size, size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let h = 160
        + (hash(x, y, seed + 1) - 0.5) * 85
        + (hash(x >> 1, y >> 1, seed + 2) - 0.5) * 60
        + (hash(x >> 2, y >> 2, seed + 3) - 0.5) * 40;

      const pit = pitDepth(x, y, seed);
      if (pit > 0) h -= pit * 220;

      const cp = crackProximity(x, y, size, seed, crack);
      if (cp < 1) h -= (1 - cp) * (crack ? 200 : 170);

      // ร่องปูนรอบขอบก้อนสี่เหลี่ยม
      const edge = Math.min(x, y, size - 1 - x, size - 1 - y);
      const mortar = Math.max(14, size * 0.055);
      if (edge < mortar) h -= (1 - edge / mortar) * 55;
      else if (edge < mortar + 8) h += ((edge - mortar) / 8) * 25;

      h = Math.max(0, Math.min(255, h));
      const i = (y * size + x) * 4;
      img.data[i] = h;
      img.data[i + 1] = h;
      img.data[i + 2] = h;
      img.data[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  return canvas;
}

export function createStoneTexture(opts = {}) {
  const {
    size = 416,
    base = [176, 108, 62],
    moss = 0,
    seed = 1,
    crack = false,
  } = opts;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(size, size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / (size - 1);
      const v = y / (size - 1);

      const n1 = hash(x, y, seed);
      const n2 = hash(x >> 1, y >> 1, seed + 3);
      const n3 = hash(x >> 2, y >> 2, seed + 7);
      const n4 = hash(x >> 3, y >> 3, seed + 13);
      const n5 = hash(x >> 4, y >> 4, seed + 17);
      const n = n1 * 0.16 + n2 * 0.26 + n3 * 0.24 + n4 * 0.2 + n5 * 0.14;

      let light = 0.28 + 0.5 * (1 - u * 0.3) * (1 - v * 0.4) + (n - 0.5) * 1.25;
      light = Math.max(0.08, Math.min(1.6, light));
      let r = base[0] * light;
      let g = base[1] * light;
      let b = base[2] * light;

      // เม็ดทรายจัด
      const grit = (hash(x, y, seed + 11) - 0.5) * 100;
      r += grit;
      g += grit * 0.68;
      b += grit * 0.4;

      // ลายเส้นหินหลายชั้น
      const veinA = Math.sin(x * 0.11 + y * 0.06 + n3 * 8);
      const veinB = Math.sin(x * 0.05 - y * 0.12 + n4 * 5);
      const veinC = Math.sin((x + y) * 0.08 + n2 * 4);
      const veinD = Math.sin(x * 0.16 + y * 0.03 + n5 * 3);
      const vein = veinA * 0.35 + veinB * 0.3 + veinC * 0.2 + veinD * 0.15;
      if (vein > 0.28) {
        const vt = Math.min(1, (vein - 0.28) / 0.55);
        r *= 1 - vt * 0.5;
        g *= 1 - vt * 0.45;
        b *= 1 - vt * 0.35;
      } else if (vein < -0.3) {
        const vt = Math.min(1, (-vein - 0.3) / 0.55);
        r = Math.min(255, r + vt * 40);
        g = Math.min(255, g + vt * 24);
        b = Math.min(255, b + vt * 10);
      }

      // ก้อนสีในเนื้อ
      if (n3 > 0.58) {
        const blot = (n3 - 0.58) / 0.42;
        r *= 1 - blot * 0.42;
        g *= 1 - blot * 0.4;
        b *= 1 - blot * 0.34;
      }
      if (n4 < 0.38) {
        const blot = (0.38 - n4) / 0.38;
        r = Math.min(255, r + blot * 42);
        g = Math.min(255, g + blot * 26);
        b = Math.min(255, b + blot * 12);
      }
      if (n5 > 0.65) {
        r *= 0.7;
        g *= 0.68;
        b *= 0.6;
      }

      // —— รูพรุนหนาแน่นจัด ——
      const pit = pitDepth(x, y, seed);
      if (pit > 0) {
        const depth = 1 - pit * 0.95;
        r *= Math.max(0.03, depth * depth);
        g *= Math.max(0.025, depth * depth * 0.94);
        b *= Math.max(0.02, depth * depth * 0.86);
        if (pit > 0.12 && pit < 0.4) {
          r = Math.min(255, r + 22);
          g = Math.min(255, g + 12);
        }
      }

      // คราบสกปรก
      const stain = hash(x >> 2, y >> 2, seed + 19);
      if (stain < 0.4 + moss * 0.15 && n > 0.25) {
        const s = 0.28 + hash(x, y, seed + 21) * 0.35;
        r *= s;
        g *= s * 0.9;
        b *= s * 0.78;
      }

      // ร่องปูนขอบก้อน
      const edge = Math.min(x, y, size - 1 - x, size - 1 - y);
      const mortar = Math.max(14, Math.floor(size * 0.055));
      if (edge < mortar) {
        const t = edge / mortar;
        const mt = 0.2 + 0.25 * hash(x >> 2, y >> 2, seed + 55);
        r = r * t + (70 * mt) * (1 - t);
        g = g * t + (64 * mt) * (1 - t);
        b = b * t + (56 * mt) * (1 - t);
      } else if (edge < mortar + 8) {
        const t = 1 - (edge - mortar) / 8;
        r = Math.min(255, r + 28 * t);
        g = Math.min(255, g + 16 * t);
        b = Math.min(255, b + 8 * t);
      }

      // —— รอยร้าวเครือข่ายหนาแน่น ——
      const cp = crackProximity(x, y, size, seed, crack);
      if (cp < 1) {
        const t = 1 - cp;
        const dark = crack ? 0.02 : 0.045;
        const mul = dark + (1 - dark) * (1 - t);
        r *= mul;
        g *= mul * 0.88;
        b *= mul * 0.78;
        if (cp > 0.3) {
          r *= 0.7;
          g *= 0.68;
          b *= 0.62;
        }
      }

      // ไฮไลต์เม็ดแร่
      if (hash(x, y, seed + 31) > 0.935) {
        r = Math.min(255, r + 90);
        g = Math.min(255, g + 60);
        b = Math.min(255, b + 28);
      }
      if (hash(x, y, seed + 41) > 0.96) {
        r = Math.min(255, r + 55);
        g = Math.min(255, g + 38);
        b = Math.min(255, b + 16);
      }

      r = (r - 118) * 1.5 + 118;
      g = (g - 118) * 1.42 + 118;
      b = (b - 118) * 1.35 + 118;

      const i = (y * size + x) * 4;
      img.data[i] = Math.max(0, Math.min(255, r));
      img.data[i + 1] = Math.max(0, Math.min(255, g));
      img.data[i + 2] = Math.max(0, Math.min(255, b));
      img.data[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;

  const bump = new THREE.CanvasTexture(createHeightCanvas(size, seed, crack));
  bump.colorSpace = THREE.NoColorSpace;
  bump.anisotropy = 8;
  bump.needsUpdate = true;
  tex.userData.bumpMap = bump;

  return tex;
}

export function createStoneMaterial(opts = {}) {
  const map = createStoneTexture(opts);
  return new THREE.MeshStandardMaterial({
    map,
    bumpMap: map.userData.bumpMap,
    bumpScale: 3.8,
    roughness: 0.97,
    metalness: 0.0,
  });
}
